<template>
  <v-row class="d-flex justify-start" v-if="canViewDormantStatus">
		<v-col cols="12">
			<v-progress-linear v-if="loading" indeterminate />
			<div v-else class="d-flex px-2" style="gap: 1rem 1.5rem; flex-wrap: wrap;">
				<template v-for="data in dormantStatusData">
					<div
						v-if="dormantStatuses[data.key]"
						:key="data.key"
						:class="{ 'dormant-status--active': value === data.key }"
						class="dormant-status"
						@click="selectStatus(data.key)"
					>
						<span class="mr-2">{{ data.label }}</span>
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<v-icon color="#1570ef" v-bind="attrs" v-on="on">icon-info-circle-q</v-icon>
							</template>
							{{ data.tooltip }}
						</v-tooltip>
						<div v-if="!value">({{ dormantStatuses[data.key].count }})</div>
						<div v-else-if="value === data.key" :class="(totalItems === undefined || !isCountVisible) && 'dormant-status-count--invisible'">({{ totalItems }})</div>
					</div>
				</template>
			</div>
		</v-col>

		<v-dialog v-model="doShowModal" width="98%" max-width="700px">
			<v-card>
				<v-card-title class="d-flex flex-nowrap pr-4">
					<v-chip color="#0083EA" text-color="white" label class="title-chip align-self-start flex-shrink-0">
						<v-icon small>icon-info-circle-q</v-icon>
					</v-chip>
					<h3 class="title-content">
						Possible Status Discrepancies
					</h3>
					<v-btn
						icon
						class="ml-2 align-self-start flex-shrink-0"
						color="#707070"
						@click="doShowModal = false"
					>
						<v-icon>icon-x-close-q</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					The Dormant Status filters reflect an agent's last login to Opt, therefore, may display a contrasting 'status tag' in HQ. This is expected due to variances in how agent statuses are tracked across both systems.
				</v-card-text>
				<v-card-actions class="flex justify-end px-6 pb-4">
					<v-btn class="action-btn" color="#46c3b2" @click="doShowModal = false">Dismiss</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import QuilityAPI from '@/store/API/QuilityAPI.js'

const STORAGE_KEY = 'dormant-status-instruction-modal'
const STORAGE_KEY_SEPARATOR = '-----'

const dormantStatusData = [
	{
		label: 'Active',
		key: 'Active',
		tooltip: 'Agent is actively logging in and out of both systems.',
	},
	{
		label: 'Dormant 15-30 Days',
		key: 'Dormant_15_30',
		tooltip: 'Agent has fallen into the “Available” status and has not logged into HQ/OPT.',
	},
	{
		label: 'Dormant 31-90 Days',
		key: 'Dormant_31_90',
		tooltip: 'Agent remains in “Available” status.',
	},
	{
		label: 'Dormant 91-180 Days',
		key: 'Dormant_91_180',
		tooltip: 'Agent remains in “Available” or “Stalled” status. (Agent falls into a “Stalled” status if they have an active downline). Home office must unlock agent in OPT.',
	},
	{
		label: 'Dormant 181-364 Days',
		key: 'Dormant_181_364',
		tooltip: 'Agent remains in “Available” or “Stalled” status (Agent falls into a “Stalled” status if they have an active downline). Home office must unlock agent in OPT and agent is in jeopardy of having to re-onboard.',
	},
	{
		label: 'Lapsed 365+',
		key: 'Lapsed',
		tooltip: 'Agent has been inactive for over 1 year (must be re-contracted through the home office).',
	},
	{
		label: 'Stalled',
		key: 'Stalled',
		tooltip: 'Agent is inactive but maintains an active downline.',
	},
]

export default {
    name: "QDormantStatuses",
	props: {
		value: {
			type: String,
			default: '',
		},
		totalItems: {
			type: Number,
		},
	},
	data() {
		return {
			dormantStatuses: {},
			dormantStatusData,
			isCountVisible: true,
			loading: false,
			doShowModal: false,
			sessionKey: '',
		}
	},
	created () {
		QuilityAPI.config().then((token) => {
			this.sessionKey = token.slice(-24)
		})
	},
	computed: {
		canViewDormantStatus() {
			return this.hasRole(['SuperAdmin', 'Staff']) || this.user.Agent.HasManagerAccess;
		},
	},
	methods: {
		selectStatus(key) {
			this.setCountVisibility(false)
			this.$emit('input', this.value === key ? '' : key)
			this.showInstructionModal()
		},
		showInstructionModal () {
			if (this.checkModalCapability()) {
				this.storeModalAppearance()
				this.doShowModal = true
			}
		},
		checkModalCapability () {
			if (!this.sessionKey) { return false }
			const storageValue = localStorage.getItem(STORAGE_KEY)
			const times = storageValue ? storageValue.split(STORAGE_KEY_SEPARATOR) : []
			if (times.length >= 3) { return  false }
			if (times.at(-1) === this.sessionKey) { return false }
			return true
		},
		storeModalAppearance () {
			const storageValue = localStorage.getItem(STORAGE_KEY)
			const updatedValue = storageValue ? `${storageValue}${STORAGE_KEY_SEPARATOR}${this.sessionKey}` : this.sessionKey
			localStorage.setItem(STORAGE_KEY, updatedValue)
		},
		// expose
		setCountVisibility (val = true) {
			this.isCountVisible = val
		},
	},
	watch: {
		canViewDormantStatus: {
			handler(newValue) {
				if (newValue) {
					this.loading = true
					QuilityAPI.getDormantStatus()
						.then((response) => {
							this.dormantStatuses = response.data
							this.loading = false
						});
				}
			},
			immediate: true,
		}
	},
}
</script>

<style lang="scss" scoped>
.dormant-status {
	border: 1px solid var(--app-color-neutral-1);
	padding: 0.75rem 1rem;
	color: var(--app-color-neutral-1);
	border-radius: 0.5rem;
	cursor: pointer;

	&--active {
		border: 1px solid #2f2f2f;
		padding: 0.75rem 1rem;
		color: #2f2f2f;
		font-weight: 600;
	}
}

.dormant-status-count {
	&--invisible {
		opacity: 0;
	}
}

.title-chip {
	height: 2.5rem;
	width: 2.5rem;
	padding: 0;
	margin: 0;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.title-content {
	flex-grow: 1;
	margin-left: 1rem;
	margin-bottom: 0;
	font-weight: 700 !important;
	font-size: calc(18px + (26 - 18) * ((100vw - 300px) / (1600 - 300)));
}

.action-btn {
	font-weight: 700;
	text-transform: none;
	box-shadow: none;
	border-radius: 0.25rem;
	border-width: 2px;
	min-height: 40px;

	::v-deep .v-btn__content {
		color: #ffffff;
	}
}
</style>